import { useMutation, useQuery } from '@tanstack/react-query';
import { DISCRETIONARY_HOURS_BUCKET_VALUES, } from '../types/entities/UserSettings.js';
import { z } from 'zod';
const planItemCountsSchema = z.object({
    tasks: z.number(),
    habits: z.number(),
    routines: z.number(),
});
const planImplementationStatusSchema = z.object({
    status: z.enum(['running', 'failed']),
    errorMessage: z.string().optional(),
});
// Zod schemas for validation
export const createGoalRequestSchema = z.object({
    goal: z.string().trim().min(1, 'Goal is required'),
    targetDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Target date must be in YYYY-MM-DD format'),
    weeklyHoursBucket: z.enum(DISCRETIONARY_HOURS_BUCKET_VALUES),
});
export const createGoalResponseSchema = z.object({
    id: z.string(),
    message: z.string(),
});
export const updateQuestionAnswerRequestSchema = z.object({
    question: z.string().trim().min(1, 'Question is required'),
    answer: z.string().trim().min(1, 'Answer is required'),
});
export const updateQuestionAnswerResponseSchema = z.object({
    message: z.string(),
});
export const realityCheckSchema = z.object({
    suggestedTargetDate: z.string(),
    suggestedTarget: z.string(),
    applied: z.boolean().optional(),
});
export const questionSchema = z.object({
    question: z.string(),
    options: z.array(z.string()),
    allowMultipleAnswers: z.boolean(),
    smartGoalConfidence: z.number(),
    actionPlanConfidence: z.number(),
    missingInfoForSMART: z.array(z.string()),
    missingInfoForActionPlan: z.array(z.string()),
    reasoningSummary: z.string().optional(),
    realityCheck: realityCheckSchema.optional(),
    answer: z.string().optional(),
});
const planItemStatusSchema = z.enum(['not_added', 'added', 'skipped']);
export const planItemSchema = z.discriminatedUnion('kind', [
    z.object({
        id: z.string(),
        status: planItemStatusSchema,
        createdId: z.string().optional(),
        kind: z.literal('task'),
        title: z.string(),
        description: z.string().optional(),
        startDate: z.string(),
        dueDate: z.string().optional(),
        rrule: z.string().optional(),
        dependsOn: z.array(z.string()).optional(),
    }),
    z.object({
        id: z.string(),
        status: planItemStatusSchema,
        createdId: z.string().optional(),
        kind: z.literal('habit'),
        name: z.string(),
        rrule: z.string(),
        startDate: z.string(),
        existingRoutineId: z.string().optional(),
    }),
    z.object({
        id: z.string(),
        status: planItemStatusSchema,
        createdId: z.string().optional(),
        kind: z.literal('routine'),
        name: z.string(),
        startDate: z.string(),
        rrule: z.string(),
        habits: z.array(z.object({ name: z.string() })),
    }),
]);
export const yourProgressSchema = z.object({
    percent: z.number(),
    source: z.enum(['user', 'follow-up']),
});
export const planMilestoneSchema = z.object({
    id: z.string(),
    milestone: z.string(),
    description: z.string().optional(),
    targetDate: z.string(),
    items: z.array(planItemSchema),
    done: z.boolean().optional(),
    yourProgress: yourProgressSchema.optional(),
});
export const goalPlanSchema = z.object({
    items: z.array(planItemSchema),
    milestones: z.array(planMilestoneSchema),
});
export const goalRefinementResponseSchema = z.object({
    id: z.string(),
    today: z.string(),
    targetDate: z.string(),
    goal: z.string(),
    description: z.string().optional(),
    originalInput: z.string(),
    questions: z.array(questionSchema),
    plan: goalPlanSchema.optional(),
    planAcceptedAt: z.coerce.date().optional(),
    done: z.boolean().optional(),
    yourProgress: yourProgressSchema.optional(),
    activeMilestoneId: z.string().optional(),
    planItemsToImplement: planItemCountsSchema.optional(),
    milestonePlanItemsToImplement: z.record(z.string(), planItemCountsSchema).optional(),
    planImplementation: planImplementationStatusSchema.optional(),
    currentStep: z.string(),
    status: z.string(),
    errorMessage: z.string().optional(),
    canRetry: z.boolean().optional(),
    retryCount: z.number(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
// TanStack Query mutation hook for creating goals
export const useCreateGoal = (request) => useMutation({
    mutationFn: (data) => {
        // Validate the request data
        const validatedData = createGoalRequestSchema.parse(data);
        return request({
            url: '/goals',
            method: 'POST',
            data: validatedData,
        }, createGoalResponseSchema);
    },
});
// TanStack Query mutation hook for updating question answers
export const useUpdateQuestionAnswer = (request) => useMutation({
    mutationFn: ({ goalId, ...data }) => {
        // Validate the request data
        const validatedData = updateQuestionAnswerRequestSchema.parse(data);
        return request({
            url: `/goals/${goalId}`,
            method: 'PATCH',
            data: validatedData,
        }, updateQuestionAnswerResponseSchema);
    },
});
export const revisePlanRequestSchema = z.object({
    feedback: z.string().refine((value) => value.trim().length > 0, 'Feedback is required'),
});
export const planActionResponseSchema = z.object({
    message: z.string(),
});
// Asks the AI to revise the proposed plan; the goal goes back to processing until it is done
export const useRevisePlan = (request) => useMutation({
    mutationFn: ({ goalId, ...data }) => {
        const validatedData = revisePlanRequestSchema.parse(data);
        return request({
            url: `/goals/${goalId}/revise-plan`,
            method: 'POST',
            data: validatedData,
        }, planActionResponseSchema);
    },
});
// Restarts a refinement that failed or got stuck; the goal goes back to processing
export const useRetryGoalRefinement = (request) => useMutation({
    mutationFn: ({ goalId }) => request({
        url: `/goals/${goalId}/retry`,
        method: 'POST',
    }, planActionResponseSchema),
});
// Accepts the proposed plan without creating any tasks or habits
export const useAcceptPlan = (request) => useMutation({
    mutationFn: ({ goalId }) => request({
        url: `/goals/${goalId}/accept-plan`,
        method: 'POST',
    }, planActionResponseSchema),
});
export const addPlanItemResponseSchema = z.object({
    createdId: z.string(),
});
const planItemUrl = ({ goalId, planItemId }, action) => `/goals/${goalId}/plan-items/${planItemId}/${action}`;
// Creates the real task, habit or routine for a plan item and marks the item added
export const useAddPlanItem = (request) => useMutation({
    mutationFn: (params) => request({ url: planItemUrl(params, 'add'), method: 'POST' }, addPlanItemResponseSchema),
});
export const completePlanItemResponseSchema = z.object({
    createdId: z.string(),
    created: z.boolean(),
    taskCompleted: z.boolean(),
    habitCompletions: z.array(z.object({
        habitId: z.string(),
        completionId: z.string(),
        previousCount: z.number(),
    })),
});
// Completes a plan item (What's Next): a task is completed, a habit or routine gets today's
// completion. An item that is not added yet is added first.
export const useCompletePlanItem = (request) => useMutation({
    mutationFn: (params) => request({ url: planItemUrl(params, 'complete'), method: 'POST' }, completePlanItemResponseSchema),
});
// Reverses a completion with the response it returned; deletes the item if the completion added it
export const useUndoPlanItemCompletion = (request) => useMutation({
    mutationFn: ({ goalId, planItemId, ...completion }) => request({
        url: planItemUrl({ goalId, planItemId }, 'undo-complete'),
        method: 'POST',
        data: completion,
    }, planActionResponseSchema),
});
// Skips a plan item that is not added
export const useSkipPlanItem = (request) => useMutation({
    mutationFn: (params) => request({ url: planItemUrl(params, 'skip'), method: 'POST' }, planActionResponseSchema),
});
// Returns a skipped plan item to not added
export const useUnskipPlanItem = (request) => useMutation({
    mutationFn: (params) => request({ url: planItemUrl(params, 'unskip'), method: 'POST' }, planActionResponseSchema),
});
export const implementPlanResponseSchema = z.object({
    started: z.boolean(),
    message: z.string().optional(),
});
// Starts a background run creating every plan item that is not added (or every one in a
// milestone), leaving skipped ones alone; the goal's planImplementation shows its progress. Safe
// to repeat.
export const useImplementPlan = (request) => useMutation({
    mutationFn: ({ goalId, milestoneId }) => request({
        url: milestoneId
            ? `/goals/${goalId}/milestones/${milestoneId}/implement-plan`
            : `/goals/${goalId}/implement-plan`,
        method: 'POST',
    }, implementPlanResponseSchema),
});
export const deleteGoalResponseSchema = z.object({
    message: z.string(),
    deletedTasks: z.number(),
    deletedHabits: z.number(),
});
// Deletes a goal with its plan. linkedItems is required when tasks or habits were added from it
export const useDeleteGoal = (request) => useMutation({
    mutationFn: ({ goalId, linkedItems }) => request({ url: `/goals/${goalId}`, method: 'DELETE', params: { linkedItems } }, deleteGoalResponseSchema),
});
// Deletes one milestone of a goal, with its plan items; the others keep their order
export const useDeleteMilestone = (request) => useMutation({
    mutationFn: ({ goalId, milestoneId, linkedItems, }) => request({
        url: `/goals/${goalId}/milestones/${milestoneId}`,
        method: 'DELETE',
        params: { linkedItems },
    }, deleteGoalResponseSchema),
});
const progressUrl = ({ goalId, milestoneId }, action) => milestoneId
    ? `/goals/${goalId}/milestones/${milestoneId}/${action}`
    : `/goals/${goalId}/${action}`;
// Sets Your Progress (0 to 100) on a milestone, or on a goal without milestones
export const useSetYourProgress = (request) => useMutation({
    mutationFn: ({ percent, ...target }) => request({ url: progressUrl(target, 'progress'), method: 'PUT', data: { percent } }, planActionResponseSchema),
});
// Marks a goal, or one of its milestones, done or not done. Done on a goal with milestones also
// marks every milestone done.
export const useSetGoalDone = (request) => useMutation({
    mutationFn: ({ done, ...target }) => request({ url: progressUrl(target, 'done'), method: 'PUT', data: { done } }, planActionResponseSchema),
});
const PLAN_IMPLEMENTATION_POLL_MS = 2000;
/** How often the refine page checks whether the AI has finished its step. */
export const GOAL_REFINEMENT_POLL_MS = 1500;
/**
 * How often it checks once the refinement looks stuck and Try again is shown: the run may still
 * finish (a queue retry), but nothing is gained by checking every second and a half.
 */
export const STALLED_GOAL_REFINEMENT_POLL_MS = 15000;
/**
 * Whether the goal is waiting on the AI rather than on the user: it is neither completed nor
 * failed, and no question is waiting for an answer. Covers a new goal, a round being processed and
 * a plan revision.
 */
export function isAwaitingAi(goal) {
    if (goal.status === 'completed' || goal.status === 'failed')
        return false;
    return !goal.questions.some((q) => !q.answer);
}
/**
 * The refetch interval for a goal, or false to stop polling. Implement Plan runs on goals that
 * finished refining, so the two never overlap.
 */
export function goalRefinementRefetchInterval(goal) {
    if (!goal)
        return false;
    if (goal.planImplementation?.status === 'running')
        return PLAN_IMPLEMENTATION_POLL_MS;
    if (isAwaitingAi(goal)) {
        return goal.canRetry ? STALLED_GOAL_REFINEMENT_POLL_MS : GOAL_REFINEMENT_POLL_MS;
    }
    return false;
}
// TanStack Query hook for fetching goal refinement details
export const useGoalRefinement = (request, goalId) => useQuery({
    queryKey: ['goalRefinement', goalId],
    queryFn: () => request({
        url: `/goals/${goalId}`,
        method: 'GET',
    }, goalRefinementResponseSchema),
    enabled: !!goalId,
    // While the AI works on the goal, or Implement Plan runs in the background, refetch so the next
    // question, the plan or the added items show without a reload. Paused while the tab is hidden.
    refetchInterval: (query) => goalRefinementRefetchInterval(query.state.data?.data),
});
// TanStack Query hook for fetching all goal refinements for the user
export const useGoalRefinements = (request) => useQuery({
    queryKey: ['goalRefinements'],
    queryFn: () => request({
        url: '/goals',
        method: 'GET',
    }, z.array(goalRefinementResponseSchema)),
});
